import { useTranslation } from "react-i18next";
import { useEstimate } from "../store/EstimateContext";
import { CHART_COLORS, DM_TYPES } from "../lib/s1000dData";
import HelpIconButton from "./HelpIconButton";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from "recharts";

function StatCard({ label, value, sub, testid, accent }) {
  return (
    <div className="bg-white border border-gray-300 p-4" data-testid={testid}>
      <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">{label}</p>
      <p
        className={
          "font-mono text-2xl font-bold leading-none mt-2 " +
          (accent ? "text-[#002FA7]" : "text-black")
        }
      >
        {value}
      </p>
      {sub && <p className="text-[10px] uppercase tracking-wider text-gray-500 mt-2">{sub}</p>}
    </div>
  );
}

export default function SummaryTab() {
  const { t } = useTranslation();
  const { estimate, totals, resourcing, formatCurrency, formatHours, formatPercent } = useEstimate();
  const fmtHours = formatHours;
  const fmtPercent = formatPercent;

  const roleData = totals.roleBreakdown
    .filter((r) => r.roleCost > 0)
    .map((r) => ({ name: r.name, value: Number(r.roleCost.toFixed(2)), pct: r.costPercent }));

  const dmData = DM_TYPES
    .map((t) => ({ name: t.name, hours: totals.perTypeHours[t.code] || 0 }))
    .filter((d) => d.hours > 0);

  const levelData = [
    { name: "Simple", DM: totals.perLevelHours.simple, Illustration: totals.illusPerLevelHours.simple },
    { name: "Intermediate", DM: totals.perLevelHours.intermediate, Illustration: totals.illusPerLevelHours.intermediate },
    { name: "Complex", DM: totals.perLevelHours.complex, Illustration: totals.illusPerLevelHours.complex },
  ];

  return (
    <section className="space-y-6" data-testid="summary-tab">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
          {t("summary.section")}
        </p>
        <h2 className="font-heading text-2xl tracking-tight font-bold text-black flex items-center gap-2">
          {t("summary.title")}
          <HelpIconButton anchor="ch4" testId="help-icon-summary" />
        </h2>
        <p className="text-sm text-gray-600 mt-1 max-w-2xl">
          {t("summary.description")}
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Total DMs" value={totals.totalDms} testid="stat-total-dms" />
        <StatCard label="Total Illustrations" value={totals.totalIllus} testid="stat-total-illus" />
        <StatCard
          label="Total Hours"
          value={fmtHours(totals.totalHours)}
          sub={`Blended ${formatCurrency(totals.blendedRate)} / hr`}
          testid="stat-total-hours"
        />
        <StatCard
          label="Base Cost"
          value={formatCurrency(totals.baseCost)}
          sub="Roles only"
          testid="stat-base-cost"
        />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          label={`Overhead (${Number(estimate.overheadPercent) || 0}%)`}
          value={formatCurrency(totals.overheadCost)}
          testid="stat-overhead"
        />
        <StatCard
          label={`Contingency (${Number(estimate.contingencyPercent) || 0}%)`}
          value={formatCurrency(totals.contingencyCost)}
          testid="stat-contingency"
        />
        <StatCard
          label="DM Hours"
          value={fmtHours(totals.dmHours)}
          sub="Authoring effort"
          testid="stat-dm-hours"
        />
        <StatCard
          label="GRAND TOTAL"
          value={formatCurrency(totals.grandTotal)}
          sub="Base + Overhead + Contingency"
          testid="stat-grand-total"
          accent
        />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="summary-resourcing-row">
        <StatCard
          label="Working Days"
          value={resourcing.workingDays || 0}
          sub={resourcing.valid ? `${resourcing.calendarDays} cal − ${resourcing.weekendsCount} wknd − ${Number(estimate.resourcePlan?.publicHolidays) || 0} hol` : "Set start & end dates"}
          testid="stat-working-days"
        />
        <StatCard
          label="Effective Capacity"
          value={fmtHours(resourcing.availableEffectiveHours)}
          sub={`${resourcing.workingDays || 0}d × ${estimate.resourcePlan?.effectiveHoursPerDay || 0}h effective`}
          testid="stat-effective-capacity"
        />
        <StatCard
          label="Required FTE"
          value={(resourcing.fteEffective || 0).toFixed(2)}
          sub={`${(resourcing.fteStandard || 0).toFixed(2)} at standard hrs`}
          testid="stat-required-fte"
          accent
        />
        <StatCard
          label="Headcount"
          value={resourcing.totalHeadcount || 0}
          sub="Σ ⌈ role FTE ⌉"
          testid="stat-headcount"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-gray-300 p-5" data-testid="role-pie-chart">
          <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
            Cost Distribution by Role
          </p>
          <h3 className="font-heading text-lg tracking-tight font-bold text-black">
            Role Contribution
          </h3>
          <div className="h-72 mt-4">
            {roleData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-gray-400 text-sm">
                Enter data modules and roles to see role cost distribution.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%" minHeight={260}>
                <PieChart>
                  <Pie
                    data={roleData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={1}
                    stroke="#fff"
                  >
                    {roleData.map((entry, index) => (
                      <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(v, n, p) => [`${formatCurrency(v)} (${fmtPercent(p.payload.pct)})`, p.payload.name]}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="bg-white border border-gray-300 p-5" data-testid="level-bar-chart">
          <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
            Hours by Complexity Level
          </p>
          <h3 className="font-heading text-lg tracking-tight font-bold text-black">
            DM vs Illustration Effort
          </h3>
          <div className="h-72 mt-4">
            <ResponsiveContainer width="100%" height="100%" minHeight={260}>
              <BarChart data={levelData}>
                <CartesianGrid stroke="#E5E7EB" strokeDasharray="0" vertical={false} />
                <XAxis dataKey="name" stroke="#6B7280" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} />
                <YAxis stroke="#6B7280" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="DM" stackId="a" fill="#002FA7" />
                <Bar dataKey="Illustration" stackId="a" fill="#FF2400" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-gray-300" data-testid="role-breakdown-table">
          <div className="border-b border-gray-300 p-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
              Per-Role Cost
            </p>
            <h3 className="font-heading text-lg tracking-tight font-bold text-black">
              Role Cost Table
            </h3>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-100 text-black font-bold uppercase tracking-wider text-[10px] border-b border-gray-300">
                <th className="py-2 px-3 text-left">Role</th>
                <th className="py-2 px-3 text-right">Hours</th>
                <th className="py-2 px-3 text-right">Cost</th>
                <th className="py-2 px-3 text-right">% of Base</th>
              </tr>
            </thead>
            <tbody>
              {totals.roleBreakdown.map((r, idx) => (
                <tr key={idx} className="border-b border-gray-200 hover:bg-[#F9FAFB]">
                  <td className="py-2 px-3 font-semibold">
                    <span
                      className="inline-block w-2 h-2 mr-2 align-middle"
                      style={{ backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] }}
                    />
                    {r.name}
                  </td>
                  <td className="py-2 px-3 font-mono text-right text-gray-700">{fmtHours(r.roleHours)}</td>
                  <td
                    className="py-2 px-3 font-mono text-right text-black font-bold"
                    data-testid={`summary-role-${idx}-cost`}
                  >
                    {formatCurrency(r.roleCost)}
                  </td>
                  <td
                    className="py-2 px-3 font-mono text-right text-gray-700"
                    data-testid={`summary-role-${idx}-pct`}
                  >
                    {fmtPercent(r.costPercent)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-black text-white font-bold uppercase tracking-wider text-[10px]">
                <td className="py-2 px-3">Total</td>
                <td className="py-2 px-3 font-mono text-right normal-case">{fmtHours(totals.totalHours)}</td>
                <td className="py-2 px-3 font-mono text-right normal-case">{formatCurrency(totals.baseCost)}</td>
                <td className="py-2 px-3 font-mono text-right normal-case">100.0%</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div className="bg-white border border-gray-300" data-testid="dm-breakdown-table">
          <div className="border-b border-gray-300 p-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
              By Data Module Type
            </p>
            <h3 className="font-heading text-lg tracking-tight font-bold text-black">
              Hours per DM Type
            </h3>
          </div>
          {dmData.length === 0 ? (
            <div className="p-8 text-gray-400 text-sm text-center">
              No data modules entered yet.
            </div>
          ) : (
            <div className="h-72 p-4">
              <ResponsiveContainer width="100%" height="100%" minHeight={260}>
                <BarChart data={dmData} layout="vertical" margin={{ left: 10 }}>
                  <CartesianGrid stroke="#E5E7EB" horizontal={false} />
                  <XAxis type="number" stroke="#6B7280" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    stroke="#6B7280"
                    tick={{ fontSize: 10 }}
                    width={140}
                  />
                  <Tooltip />
                  <Bar dataKey="hours" fill="#111827" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
